/**
 * The label of a step's item. A template step stores the item's KEY
 * (`assetRef`: a survey type or a resource key) and a `name` the author
 * typed. The author's name is the label: it is what the template says
 * ("Eat Well - Audio"), and the same words must appear on every screen
 * that lists the step. The catalogues - GET /surveys (`{ id, title }`)
 * and GET /resources (`{ resourceKey, title }`) - only stand in when
 * there is no real name: seeded templates carry the raw key as the name
 * ("metabolicLessons"), and that is the one case where the catalogue's
 * title reads better.
 */
const lower = value => String(value ?? '').toLowerCase();

/** `{ <lower key>: title }` over both catalogues. */
export const buildAssetTitleIndex = (surveys, resources) => {
  const index = {};
  (Array.isArray(surveys) ? surveys : []).forEach(survey => {
    if (survey?.id && survey.title) index[lower(survey.id)] = survey.title;
  });
  (Array.isArray(resources) ? resources : []).forEach(resource => {
    if (resource?.resourceKey && resource.title)
      index[lower(resource.resourceKey)] = resource.title;
  });
  return index;
};

// A name that is just the key, in any case or spacing, is not a name.
const isRawKey = (name, assetRef) => lower(name).trim() === lower(assetRef).trim();

/**
 * The step's own name; the catalogue title when the name is missing or
 * is the bare key; the key itself as the last resort. `index` comes
 * from buildAssetTitleIndex.
 */
export const assetTitleOf = (step, index) => {
  const name = String(step?.name ?? '').trim();
  const assetRef = step?.assetRef;
  if (name && !isRawKey(name, assetRef)) return name;
  const fromCatalogue = index && assetRef ? index[lower(assetRef)] : null;
  return fromCatalogue || name || assetRef || '';
};
